import { cx, memberStyle } from './color.js';
import styles from './DayHighlights.module.css';
import { Illustration } from './Illustration.js';
import { Pictogram } from './Pictogram.js';

// Dagmarkeringen in de hele-dag-band (w4; docs/DESIGN.md §8.F): verjaardag in de lidtint,
// feestdag en gezinsdag op een verzonken vlak, schoolvakantie in de info-tint. Kiosk-veilig
// (geen flex gap); de server levert alles voorgeformatteerd (`days[].highlights`).

export interface DayHighlightItem {
  kind:
    'birthday' | 'public_holiday' | 'special_day' | 'school_holiday' | 'faith_day' | 'culture_day';
  title: string;
  icon: string;
  color_key: string | null;
  day_index: number;
  day_count: number;
  household_label?: string | null;
  /** Korte uitleg van de server ('Maanzicht in Marokko: di 9 of wo 10 mrt. ...'), of null. */
  detail?: string | null | undefined;
  /** Naam zonder '(verwacht)' (golf 2). */
  base_title?: string | undefined;
  /** Nog niet vast ('(verwacht)'; golf 2). */
  expected?: boolean | undefined;
  /** Tweede regel die de app zelf formatteert ('di 9 of wo 10 mrt'), alleen web. */
  note?: string | null | undefined;
}

export interface DayHighlightsProps {
  items: ReadonlyArray<DayHighlightItem>;
  size?: 'kiosk' | 'web';
  /** Hooguit zoveel pillen; de rest als "+n" (kiosk: 2). */
  max?: number;
  /** "dag {index} van {count}" (alleen web, meerdaags). */
  dayOfLabel?: (index: number, count: number) => string;
  /** Kleine illustratie bij een verjaardag vandaag (kiosk). */
  celebrate?: boolean;
  name?: string;
  /**
   * Alleen web: een markering met uitleg (detail/note) wordt een knop die dit aanroept (blad met
   * uitleg en 'bevestigen'). Zonder: gewone pillen (kiosk).
   */
  onSelect?: (item: DayHighlightItem) => void;
}

export function DayHighlights({
  items,
  size = 'web',
  max,
  dayOfLabel,
  celebrate,
  name,
  onSelect,
}: DayHighlightsProps) {
  if (items.length === 0) return null;
  const limit = max ?? (size === 'kiosk' ? 2 : items.length);
  const shown = items.slice(0, limit);
  const rest = items.length - shown.length;
  const picto = size === 'kiosk' ? 32 : 20;
  const hasBirthday = celebrate && items.some((h) => h.kind === 'birthday');
  return (
    <div class={cx(styles.band, styles[size])} data-name={name ?? 'day-highlights'}>
      {hasBirthday ? (
        <Illustration id="birthday" width={size === 'kiosk' ? 64 : 48} class={styles.art} />
      ) : null}
      {shown.map((h, i) => {
        const inner = (
          <>
            <Pictogram icon={h.icon} label={h.title} size={picto} decorative />
            <span class={cx(styles.text, size === 'web' && h.note && styles.twoLine)}>
              {h.title}
              {h.household_label ? <span class={styles.meta}> · {h.household_label}</span> : null}
              {size === 'web' && h.note ? (
                <span class={cx(styles.meta, styles.noteLine)} data-name="highlight-note">
                  {h.note}
                </span>
              ) : null}
              {size === 'web' && h.day_count > 1 && dayOfLabel ? (
                <span class={styles.meta}> · {dayOfLabel(h.day_index, h.day_count)}</span>
              ) : null}
            </span>
          </>
        );
        const key = `${h.kind}-${h.title}-${i}`;
        const style = h.kind === 'birthday' ? memberStyle(h.color_key) : undefined;
        const hint = [h.note, h.detail].filter(Boolean).join(' · ') || undefined;
        if (size === 'web' && onSelect && (h.detail || h.note)) {
          return (
            <button
              key={key}
              type="button"
              class={cx(styles.pill, styles.tap, styles[h.kind])}
              style={style}
              data-kind={h.kind}
              data-name="highlight-open"
              title={hint}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(h);
              }}
            >
              {inner}
            </button>
          );
        }
        return (
          <span
            key={key}
            class={cx(styles.pill, styles[h.kind])}
            style={style}
            data-kind={h.kind}
            title={size === 'web' ? hint : undefined}
          >
            {inner}
          </span>
        );
      })}
      {rest > 0 ? <span class={cx(styles.pill, styles.more)}>+{rest}</span> : null}
    </div>
  );
}
